import { useRef, useState, useEffect, useCallback } from 'react';

interface ParallaxState {
  carouselProgress: number; // 0 = fully visible, 1 = scrolled past
  bentoProgress: number; // 0 = not yet visible, 1 = fully in view
  setupProgress: number; // 0 = not yet visible, 1 = fully in view
  ctaProgress: number; // 0 = not yet visible, 1 = fully in view
  carouselStyle: React.CSSProperties;
  bentoStyle: React.CSSProperties;
  setupStyle: React.CSSProperties;
  ctaStyle: React.CSSProperties;
}

interface UseScrollParallaxOptions {
  disabled?: boolean;
}

/**
 * Hook for creating scroll-driven parallax transitions between sections.
 *
 * Returns:
 * - carouselRef: Attach to the carousel/exiting section
 * - bentoRef: Attach to the bento/entering section
 * - carouselStyle: Transform styles for the exiting section
 * - bentoOffset: Pixel offset for entering section elements
 */
export function useScrollParallax(options: UseScrollParallaxOptions = {}) {
  const { disabled = false } = options;

  const carouselRef = useRef<HTMLDivElement>(null);
  const bentoRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const [state, setState] = useState<ParallaxState>({
    carouselProgress: 0,
    bentoProgress: 0,
    carouselStyle: {},
    bentoStyle: {
      transform: 'scale(0.85) translateY(60px)',
      opacity: 0.3,
      filter: 'blur(8px)',
    },
  });

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

  const calculateParallax = useCallback(() => {
    if (disabled || prefersReducedMotion) {
      setState({
        carouselProgress: 0,
        bentoProgress: 1,
        carouselStyle: {},
        bentoStyle: {},
      });
      return;
    }

    const viewportHeight = window.innerHeight;

    // Calculate carousel exit progress
    let carouselProgress = 0;
    let carouselStyle: React.CSSProperties = {};

    if (carouselRef.current) {
      const rect = carouselRef.current.getBoundingClientRect();
      // Progress based on how much the carousel bottom has moved above viewport center
      // 0 = carousel bottom at or below viewport center
      // 1 = carousel bottom at or above viewport top
      const carouselBottom = rect.bottom;
      const transitionStart = viewportHeight * 0.6; // Start when bottom hits 60% from top
      const transitionEnd = viewportHeight * 0.1; // End when bottom hits 10% from top

      if (carouselBottom < transitionStart) {
        carouselProgress = Math.min(1, Math.max(0,
          (transitionStart - carouselBottom) / (transitionStart - transitionEnd)
        ));
      }

      // Apply carousel exit transforms
      if (carouselProgress > 0) {
        const scale = 1 - (carouselProgress * 0.15); // 1 → 0.85
        const opacity = 1 - (carouselProgress * 0.7); // 1 → 0.3
        const blur = carouselProgress * 8; // 0 → 8px

        carouselStyle = {
          transform: `scale(${scale})`,
          opacity: opacity,
          filter: `blur(${blur}px)`,
          transformOrigin: 'center bottom',
        };
      }
    }

    // Calculate bento entrance progress (OPPOSITE of carousel exit)
    let bentoProgress = 0;
    let bentoStyle: React.CSSProperties = {
      transform: 'scale(0.85) translateY(60px)',
      opacity: 0.3,
      filter: 'blur(8px)',
      transformOrigin: 'center top',
    };

    if (bentoRef.current) {
      const rect = bentoRef.current.getBoundingClientRect();
      // Progress based on how much the bento top has entered the viewport
      // 0 = bento top at or below viewport bottom
      // 1 = bento top at or above viewport center
      const bentoTop = rect.top;
      const transitionStart = viewportHeight * 0.95; // Start when top enters viewport
      const transitionEnd = viewportHeight * 0.35; // Complete when top reaches 35% from top

      if (bentoTop < transitionStart) {
        bentoProgress = Math.min(1, Math.max(0,
          (transitionStart - bentoTop) / (transitionStart - transitionEnd)
        ));
      }

      // Apply bento entrance transforms (INVERSE of carousel exit)
      // As progress goes 0 → 1:
      // scale: 0.85 → 1
      // translateY: 60px → 0px
      // opacity: 0.3 → 1
      // blur: 8px → 0px
      const scale = 0.85 + (bentoProgress * 0.15); // 0.85 → 1
      const translateY = (1 - bentoProgress) * 60; // 60px → 0px
      const opacity = 0.3 + (bentoProgress * 0.7); // 0.3 → 1
      const blur = (1 - bentoProgress) * 8; // 8px → 0px

      bentoStyle = {
        transform: `scale(${scale}) translateY(${translateY}px)`,
        opacity: opacity,
        filter: `blur(${blur}px)`,
        transformOrigin: 'center top',
      };
    }

    setState({
      carouselProgress,
      bentoProgress,
      carouselStyle,
      bentoStyle,
    });
  }, [disabled, prefersReducedMotion]);

  const handleScroll = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(calculateParallax);
  }, [calculateParallax]);

  useEffect(() => {
    if (disabled || prefersReducedMotion) {
      return;
    }

    // Initial calculation
    calculateParallax();

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleScroll, calculateParallax, disabled, prefersReducedMotion]);

  return {
    carouselRef,
    bentoRef,
    ...state,
    prefersReducedMotion,
  };
}

/**
 * Helper to determine parallax speed based on grid position
 * Returns 'slow' | 'medium' | 'fast'
 */
export function getParallaxSpeed(
  colIndex: number,
  rowIndex: number,
  totalCols: number,
  totalRows: number
): 'slow' | 'medium' | 'fast' {
  const isCorner =
    (colIndex === 0 || colIndex === totalCols - 1) &&
    (rowIndex === 0 || rowIndex === totalRows - 1);

  const isEdge =
    colIndex === 0 ||
    colIndex === totalCols - 1 ||
    rowIndex === 0 ||
    rowIndex === totalRows - 1;

  if (isCorner) return 'slow';
  if (isEdge) return 'medium';
  return 'fast';
}
